import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Calendar as CalendarIcon, Bell, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format, parseISO, eachDayOfInterval, addDays, startOfDay } from 'date-fns';

function BlockedDatesBadges({ blockedSlots }) {
  const uniqueDates = useMemo(
    () => [...new Set(blockedSlots.map(s => s.date))].sort(),
    [blockedSlots]
  );
  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      <p className="text-xs font-semibold text-red-500 mb-1">Unavailable Dates:</p>
      <div className="flex flex-wrap gap-1">
        {uniqueDates.map((d) => (
          <Badge key={d} variant="outline" className="text-[10px] border-red-200 text-red-500">{d}</Badge>
        ))}
      </div>
    </div>
  );
}

export function FacilitiesModal({
  showFacilitiesModal,
  setShowFacilitiesModal,
  facilities,
  bookingNotifications,
  handleBookFacility
}) {
  return (
    <Dialog open={showFacilitiesModal} onOpenChange={setShowFacilitiesModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="border-b border-slate-200 pb-4 -mx-6 -mt-2 px-6 mb-4">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <CalendarIcon className="w-5 h-5 text-gk-navy" />
            Community Facilities ({facilities.length})
          </DialogTitle>
          <DialogDescription className="text-slate-500">Book guest rooms and community facilities</DialogDescription>
        </DialogHeader>

        {bookingNotifications.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="booking-notifications-section">
            <h4 className="text-sm font-semibold text-gk-navy flex items-center gap-1.5">
              <Bell className="w-4 h-4" />
              Booking Updates
            </h4>
            {bookingNotifications.slice(0, 5).map((notif) => (
              <div
                key={notif.id}
                data-testid={`booking-notif-${notif.id}`}
                className={`p-3 rounded-lg border text-sm ${
                  notif.type === 'booking_approved'
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                <div className="flex items-start gap-2">
                  {notif.type === 'booking_approved' ? (
                    <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 mt-0.5 text-red-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug">{notif.message}</p>
                    <p className="text-[11px] opacity-70 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-4 py-2">
          {facilities.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No facilities available</p>
          ) : (
            facilities.map((facility) => (
              <div
                key={facility.id}
                data-testid={`facility-item-${facility.id}`}
                className="p-4 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-bold text-base">{facility.name}</h4>
                    {facility.room_type && (
                      <Badge variant="outline" className="mt-1">{facility.room_type}</Badge>
                    )}
                  </div>
                  {facility.charges_per_night ? (
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-600">&#8377;{facility.charges_per_night?.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-400">per night</p>
                    </div>
                  ) : null}
                </div>
                <p className="text-sm text-slate-600 mb-2">{facility.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Capacity: {facility.capacity}</span>
                  <Button
                    size="sm"
                    onClick={() => { setShowFacilitiesModal(false); handleBookFacility(facility); }}
                    className="bg-gk-teal hover:bg-gk-teal-hover"
                  >
                    Book Now
                  </Button>
                </div>
                {facility.blocked_slots && facility.blocked_slots.length > 0 && (
                  <BlockedDatesBadges blockedSlots={facility.blocked_slots} />
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Compute dates that should be disabled (fully booked or blocked) */
function getUnavailableDates(facility) {
  if (!facility) return [];
  const disabled = [];
  const capacity = facility.capacity || 1;

  // 1. All blocked dates
  (facility.blocked_slots || []).forEach(b => {
    try { disabled.push(startOfDay(parseISO(b.date))); } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Invalid blocked date:', b.date); }
  });

  // 2. Count bookings per date across check-in→checkout ranges
  const dateCounts = {};
  (facility.bookings || [])
    .filter(b => b.status !== 'rejected')
    .forEach(b => {
      try {
        const start = startOfDay(parseISO(b.date));
        const end = startOfDay(parseISO(b.checkout_date || b.date));
        const lastStay = end > start ? addDays(end, -1) : start;
        eachDayOfInterval({ start, end: lastStay }).forEach(d => {
          const key = format(d, 'yyyy-MM-dd');
          dateCounts[key] = (dateCounts[key] || 0) + 1;
        });
      } catch (e) { process.env.NODE_ENV !== 'production' && console.error('Invalid booking dates:', b.date, b.checkout_date); }
    });

  // Dates where bookings >= capacity
  Object.entries(dateCounts).forEach(([dateStr, count]) => {
    if (count >= capacity) {
      disabled.push(startOfDay(parseISO(dateStr)));
    }
  });

  return disabled;
}

const DISABLED_DAY_CLASS = { day_disabled: "text-muted-foreground opacity-50 line-through" };

function DatePickerField({ label, testId, selectedDate, onSelect, disabledDates, minDate }) {
  const [open, setOpen] = useState(false);
  const selected = selectedDate ? startOfDay(parseISO(selectedDate)) : undefined;
  const fromDate = minDate ? startOfDay(parseISO(minDate)) : startOfDay(new Date());

  const disabledMatcher = useMemo(
    () => [{ before: fromDate }, ...disabledDates],
    [fromDate, disabledDates]
  );

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={testId}
            type="button"
            className="w-full flex items-center justify-between p-2.5 border rounded-lg text-sm bg-white hover:bg-slate-50 transition-colors"
          >
            <span className={selected ? 'text-slate-900' : 'text-slate-400'}>
              {selected ? format(selected, 'dd MMM yyyy (EEE)') : 'Select date...'}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              if (date) { onSelect(format(date, 'yyyy-MM-dd')); }
              setOpen(false);
            }}
            disabled={disabledMatcher}
            defaultMonth={selected || fromDate}
            classNames={DISABLED_DAY_CLASS}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function BookingModal({
  showBookingModal,
  handleCloseBookingModal,
  selectedFacility,
  bookingForm,
  setBookingForm,
  handleConfirmBooking
}) {
  const unavailableDates = useMemo(
    () => getUnavailableDates(selectedFacility),
    [selectedFacility]
  );

  const checkoutMinDate = bookingForm.date
    ? format(addDays(parseISO(bookingForm.date), 1), 'yyyy-MM-dd')
    : null;

  return (
    <Dialog open={showBookingModal} onOpenChange={handleCloseBookingModal}>
      <DialogContent className="max-w-md">
        <DialogHeader className="bg-gk-navy-light text-white rounded-t-xl -mx-6 -mt-6 px-6 py-4">
          <DialogTitle className="text-white">Book {selectedFacility?.name}</DialogTitle>
        </DialogHeader>
        {selectedFacility && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">{selectedFacility.description}</p>
              <p className="text-xs text-slate-500">Rooms: {selectedFacility.capacity}</p>
              {(selectedFacility.checkin_time || selectedFacility.checkout_time) && (
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-emerald-700 font-medium">
                    Check-in: {selectedFacility.checkin_time || '11:00 AM'} | Check-out: {selectedFacility.checkout_time || '11:00 AM'}
                  </p>
                </div>
              )}
            </div>

            {selectedFacility.booking_type !== 'slot' ? (
              <>
                <DatePickerField
                  label="Check-in Date"
                  testId="booking-checkin-input"
                  selectedDate={bookingForm.date}
                  onSelect={(d) => setBookingForm({...bookingForm, date: d, checkout_date: ''})}
                  disabledDates={unavailableDates}
                />
                <DatePickerField
                  label="Check-out Date"
                  testId="booking-checkout-input"
                  selectedDate={bookingForm.checkout_date}
                  onSelect={(d) => setBookingForm({...bookingForm, checkout_date: d})}
                  disabledDates={unavailableDates}
                  minDate={checkoutMinDate}
                />
                {bookingForm.date && bookingForm.checkout_date && (
                  <div className="p-3 bg-gk-bg rounded-lg border border-slate-200">
                    <p className="text-sm font-semibold text-gk-navy mb-1">Rental Summary</p>
                    <p className="text-xs text-gk-navy-light">
                      Check-in: {format(parseISO(bookingForm.date), 'dd MMM yyyy')} at {selectedFacility.checkin_time}
                    </p>
                    <p className="text-xs text-gk-navy-light">
                      Check-out: {format(parseISO(bookingForm.checkout_date), 'dd MMM yyyy')} at {selectedFacility.checkout_time}
                    </p>
                    <p className="text-xs text-gk-teal mt-2 font-medium">
                      Total Days: {Math.ceil((new Date(bookingForm.checkout_date) - new Date(bookingForm.date)) / (1000 * 60 * 60 * 24))}
                    </p>
                    {selectedFacility.charges_per_night ? (
                      <p className="text-xs text-emerald-700 font-bold mt-1">
                        Estimated Total: &#8377;{(selectedFacility.charges_per_night * Math.max(Math.ceil((new Date(bookingForm.checkout_date) - new Date(bookingForm.date)) / (1000 * 60 * 60 * 24)), 1)).toLocaleString()}
                      </p>
                    ) : null}
                  </div>
                )}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-800 font-medium">
                    Confirmation will be sent to your email upon Management Committee approval
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Comments / Special Requests (optional)</label>
                  <textarea
                    data-testid="booking-comments-input"
                    value={bookingForm.comments || ''}
                    onChange={(e) => setBookingForm({...bookingForm, comments: e.target.value})}
                    className="w-full p-2 border rounded-lg text-sm resize-none"
                    rows={2}
                    placeholder="e.g., Need extra mattress, early check-in, etc."
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Date</label>
                  <input
                    data-testid="booking-date-input"
                    type="date"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Time Slot</label>
                  <select
                    data-testid="booking-slot-select"
                    value={bookingForm.slot}
                    onChange={(e) => setBookingForm({...bookingForm, slot: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">Choose a slot</option>
                    {selectedFacility.available_slots?.map((slot, idx) => (
                      <option key={idx} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="flex gap-2">
              <Button
                data-testid="confirm-booking-button"
                onClick={handleConfirmBooking}
                className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
              >
                Submit Booking Request
              </Button>
              <Button
                onClick={() => handleCloseBookingModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
